import {
  Injectable,
  signal,
  computed,
  inject,
  DestroyRef,
} from "@angular/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from "@angular/forms";
import { forkJoin, of, Observable } from "rxjs";
import { switchMap, map, catchError, startWith } from "rxjs/operators";

import { Company } from "../../../api/models/company";
import { MeetDay } from "../../../api/models/meet-day";
import { MeetDayRound } from "../../../api/models/meet-day-round";
import { MeetDayStudent } from "../../../api/models/meet-day-student";
import { MeetDayService } from "../../../api/services/meet-day.service";
import { FileDownloadService } from "../../../shared/services/file-download.service";
import { ModalService } from "../../../shared/services/modal.service";
import { environment } from "../../../../environments/environment";
import {
  roundTimeValidator,
  roundOverlapValidator,
} from "./meetday.validators";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";

/**
 * Facade service for the Meetday Modal.
 *
 * Responsibilities:
 * - Core entity state (meetday data)
 * - Main form management (meetDayForm, roundsFormArray, companyLimitsFormArray)
 * - Save operations (create/update meetday)
 * - Cross-tab coordination (reloading data, syncing company limits)
 * - Student registrations (loading, exporting)
 *
 * Tab-specific logic is delegated to tab services:
 * - MeetdayTabEmailsService: Email scheduling
 * - MeetdayTabCompaniesService: Company registration/limits
 */
@Injectable()
export class MeetdayModalFacade {
  // Services
  private fb = inject(FormBuilder);
  private meetDayService = inject(MeetDayService);
  private fileDownloadService = inject(FileDownloadService);
  private modal = inject(ModalService);
  private destroyRef = inject(DestroyRef);

  // ========== State Signals ==========
  entity = signal<MeetDay>({
    name: "",
    address: {
      street: "",
      number: "",
      numberSuffix: null,
      postalCode: "",
      city: "",
      country: "",
    },
    date: "",
    companiesPerStudent: null,
    registeredCompanies: [],
    targetGroups: [],
  });

  registeredStudents = signal<MeetDayStudent[]>([]);

  // ========== Reactive Forms (initialized immediately for toSignal) ==========
  meetDayForm: FormGroup = this.fb.group({
    name: ["", [Validators.required]],
    date: ["", [Validators.required]],
    address: this.fb.group({
      street: ["", [Validators.required]],
      number: ["", [Validators.required]],
      numberSuffix: [""],
      postalCode: ["", [Validators.required]],
      city: ["", [Validators.required]],
      country: ["", [Validators.required]],
    }),
    companiesPerStudent: new FormControl<number | null>(null, { updateOn: 'blur' }),
    studentsPerMeetdayRound: new FormControl<number | null>(null, { updateOn: 'blur' }),
    maximumAmountOfStudentsForMeetDay: [null],
    meetDayRounds: this.fb.array([], [roundOverlapValidator()]),
    companyLimits: this.fb.array([]),
    targetGroups: new FormControl<string[]>([]),
  });

  // ========== Form-to-Signal Bridges (using toSignal for reactivity) ==========
  private formStatus = toSignal(
    this.meetDayForm.statusChanges.pipe(startWith(this.meetDayForm.status)),
    { initialValue: this.meetDayForm.status }
  );

  companiesPerStudentValue = toSignal(
    this.meetDayForm
      .get("companiesPerStudent")!
      .valueChanges.pipe(
        startWith(this.meetDayForm.get("companiesPerStudent")!.value)
      ),
    { initialValue: null as number | null }
  );

  studentsPerMeetdayRoundValue = toSignal(
    this.meetDayForm
      .get("studentsPerMeetdayRound")!
      .valueChanges.pipe(
        startWith(this.meetDayForm.get("studentsPerMeetdayRound")!.value)
      ),
    { initialValue: null as number | null }
  );

  private maxStudentsValue = toSignal(
    this.meetDayForm
      .get("maximumAmountOfStudentsForMeetDay")!
      .valueChanges.pipe(
        startWith(
          this.meetDayForm.get("maximumAmountOfStudentsForMeetDay")!.value
        )
      ),
    { initialValue: null as number | null }
  );

  // ========== Computed Signals ==========
  isFormValid = computed(() => this.formStatus() === "VALID");

  /**
   * Meetday mode: 'simple' (students select companies) or 'rounds' (timed sessions)
   * Determined by which setting has a non-null value.
   */
  meetdayMode = computed<"simple" | "rounds">(() => {
    const roundLimit = this.studentsPerMeetdayRoundValue();
    return roundLimit !== null ? "rounds" : "simple";
  });

  /** Whether to show the Rounds tab (only in rounds mode) */
  showRoundsTab = computed(() => this.meetdayMode() === "rounds");

  isCompanySelectionEnabled = computed(
    () => this.companiesPerStudentValue() !== null
  );

  globalRoundLimit = computed(() => this.studentsPerMeetdayRoundValue());

  isGlobalRoundLimitValid = computed(() => {
    const value = this.globalRoundLimit();
    return value !== null && value !== undefined && value >= 1;
  });

  isMaxStudentsEnabled = computed(() => this.maxStudentsValue() !== null);

  isRoundLimitEnabled = computed(
    () => this.studentsPerMeetdayRoundValue() !== null
  );

  // ========== Form Accessors ==========
  get roundsFormArray(): FormArray {
    return this.meetDayForm.get("meetDayRounds") as FormArray;
  }

  get companyLimitsFormArray(): FormArray {
    return this.meetDayForm.get("companyLimits") as FormArray;
  }

  // ========== Mode Switching ==========

  /**
   * Switch between 'simple' and 'rounds' mode.
   * - Simple: Students select companies (companiesPerStudent is set)
   * - Rounds: Timed sessions with capacity limits (studentsPerMeetdayRound is set)
   *
   * Value semantics: null = disabled, 0 = unlimited, 1+ = specific limit
   *
   * When switching to simple mode with existing rounds, shows a confirmation dialog.
   */
  async setMeetdayMode(mode: "simple" | "rounds"): Promise<boolean> {
    if (mode === "rounds") {
      this.meetDayForm.get("companiesPerStudent")?.setValue(null);
      this.meetDayForm.get("studentsPerMeetdayRound")?.setValue(0); // 0 = unlimited
      return true;
    }

    // Switching to simple mode - confirm if rounds exist
    if (this.roundsFormArray.length > 0) {
      const confirmed = await this.modal.Confirm(
        marker("Switch to simple mode"),
        marker(
          "Switching to simple mode will delete all existing rounds. Are you sure you want to continue?"
        ),
        {}, // data
        [], // buttons
        { centered: true }, // options
        false // dismissAll - keep parent modal open
      );
      if (!confirmed) {
        return false;
      }
    }

    this.meetDayForm.get("studentsPerMeetdayRound")?.setValue(null);
    this.roundsFormArray.clear();
    this.meetDayForm.get("companiesPerStudent")?.setValue(0);
    return true;
  }

  // ========== Initialization ==========

  /**
   * Initialize the facade with entity data.
   * Call this from the modal's ngOnInit.
   */
  initialize(entityData: MeetDay): void {
    this.entity.set({ ...entityData });
    this.populateFormFromEntity(entityData);
  }

  private populateFormFromEntity(entity: MeetDay): void {
    // Determine default mode for new meetdays (no id = new)
    // Default to simple mode with unlimited (0) if neither value is set
    const isNewMeetday = !entity.id;
    const hasRoundsMode =
      entity.studentsPerMeetdayRound !== null &&
      entity.studentsPerMeetdayRound !== undefined;
    const hasSimpleMode =
      entity.companiesPerStudent !== null &&
      entity.companiesPerStudent !== undefined;

    let companiesPerStudent = entity.companiesPerStudent ?? null;
    let studentsPerMeetdayRound = entity.studentsPerMeetdayRound ?? null;

    // For new meetdays with no mode set, default to simple mode
    if (isNewMeetday && !hasRoundsMode && !hasSimpleMode) {
      companiesPerStudent = 0; // 0 = unlimited in simple mode
    }

    this.meetDayForm.patchValue({
      name: entity.name || "",
      date: entity.date || "",
      address: {
        street: entity.address?.street || "",
        number: entity.address?.number || "",
        numberSuffix: entity.address?.numberSuffix || "",
        postalCode: entity.address?.postalCode || "",
        city: entity.address?.city || "",
        country: entity.address?.country || "",
      },
      maximumAmountOfStudentsForMeetDay:
        entity.maximumAmountOfStudentsForMeetDay ?? null,
      targetGroups: entity.targetGroups || [],
    });

    // Use setValue for mode fields to ensure signals are properly triggered
    this.meetDayForm.get("companiesPerStudent")?.setValue(companiesPerStudent);
    this.meetDayForm
      .get("studentsPerMeetdayRound")
      ?.setValue(studentsPerMeetdayRound);

    // Populate rounds FormArray - using API types directly
    if (entity.meetDayRounds) {
      entity.meetDayRounds.forEach((round: MeetDayRound) => {
        this.roundsFormArray.push(this.createRoundFormGroup(round));
      });
    }

    this.syncCompanyLimits(entity.registeredCompanies || []);
  }

  // ========== Save Operations ==========

  /**
   * Save the meetday (create or update).
   * Returns an Observable that completes when done.
   */
  save(): Observable<boolean> {
    if (!this.meetDayForm.valid) {
      this.meetDayForm.markAllAsTouched();
      return of(false);
    }

    const formValue = this.meetDayForm.value;
    const entity = this.entity();

    // NgbDatepicker with adapter returns ISO date string directly
    const dateValue = formValue.date;

    // Form now stores ISO directly via DateTimePickerComponent
    const updatedEntity: MeetDay = {
      ...entity,
      name: formValue.name,
      date: dateValue,
      address: formValue.address,
      companiesPerStudent: formValue.companiesPerStudent,
      studentsPerMeetdayRound: formValue.studentsPerMeetdayRound,
      maximumAmountOfStudentsForMeetDay:
        formValue.maximumAmountOfStudentsForMeetDay,
      meetDayRounds: formValue.meetDayRounds.map((round: MeetDayRound) => ({
        id: round.id || undefined,
        roundTimeStart: round.roundTimeStart,
        roundTimeEnd: round.roundTimeEnd,
      })),
      targetGroups: formValue.targetGroups,
    };

    if (entity.id) {
      return this.updateMeetday(entity.id, updatedEntity);
    } else {
      return this.createMeetday(updatedEntity);
    }
  }

  private updateMeetday(id: string, entity: MeetDay): Observable<boolean> {
    return this.meetDayService.updateMeetDay({ id, body: entity }).pipe(
      switchMap(() => this.saveCompanyLimits(id)),
      map(() => true),
      catchError((error) => {
        console.error("Failed to update meetday:", error);
        this.modal.Alert(
          "Save Failed",
          "Failed to update event. Please check your input and try again."
        );
        return of(false);
      })
    );
  }

  private createMeetday(entity: MeetDay): Observable<boolean> {
    const { id: _, ...entityWithoutId } = entity;
    return this.meetDayService.createMeetDay({ body: entityWithoutId }).pipe(
      switchMap((createdMeetDay) => {
        if (createdMeetDay?.id) {
          return this.saveCompanyLimits(createdMeetDay.id);
        }
        return of(true);
      }),
      map(() => true),
      catchError((error) => {
        console.error("Failed to create meetday:", error);
        this.modal.Alert(
          "Save Failed",
          "Failed to create event. Please check your input and try again."
        );
        return of(false);
      })
    );
  }

  private saveCompanyLimits(meetDayId: string): Observable<boolean> {
    const limitsToUpdate = this.companyLimitsFormArray.controls
      .map((control) => ({
        companyId: control.get("companyId")?.value,
        limit: control.get("limit")?.value,
        isOverride: control.get("isOverride")?.value,
      }))
      .filter((item) => item.isOverride && item.companyId);

    if (limitsToUpdate.length === 0) {
      return of(true);
    }

    const requests = limitsToUpdate.map((item) =>
      this.meetDayService
        .updateStudentCapForCompany({
          id: meetDayId,
          body: {
            companyId: item.companyId!,
            maxAmountOfStudentsForCompany: item.limit,
          },
        })
        .pipe(
          catchError((error) => {
            console.error(
              `Failed to update limit for company ${item.companyId}:`,
              error
            );
            return of(null);
          })
        )
    );

    return forkJoin(requests).pipe(
      map((results) => {
        const failures = results.filter((r) => r === null).length;
        if (failures > 0) {
          this.modal.Alert(
            "Partial Success",
            `Updated ${results.length - failures} of ${
              results.length
            } company limits. Some failed.`
          );
        }
        return failures === 0;
      })
    );
  }

  // ========== Round Management ==========

  addRound(): void {
    const roundGroup = this.createRoundFormGroup();
    this.roundsFormArray.push(roundGroup);
  }

  removeRound(index: number): void {
    this.roundsFormArray.removeAt(index);
  }

  private createRoundFormGroup(round?: MeetDayRound): FormGroup {
    return this.fb.group(
      {
        id: [round?.id || null],
        roundTimeStart: [round?.roundTimeStart || null, [Validators.required]],
        roundTimeEnd: [round?.roundTimeEnd || null, [Validators.required]],
      },
      {
        validators: [roundTimeValidator()],
      }
    );
  }

  // ========== Company Limits Management (used by Companies tab) ==========

  private syncCompanyLimits(companies?: Company[] | null): void {
    const list = companies ?? this.entity().registeredCompanies ?? [];

    const existingLimits = new Map<
      string,
      { limit: number | null; isOverride: boolean }
    >();
    this.companyLimitsFormArray.controls.forEach((control) => {
      const companyId = control.get("companyId")?.value;
      const limit = control.get("limit")?.value;
      const isOverride = control.get("isOverride")?.value;
      if (companyId) {
        existingLimits.set(companyId, { limit, isOverride });
      }
    });

    this.companyLimitsFormArray.clear();

    list.forEach((company) => {
      if (!company?.id) {
        return;
      }

      const existing = existingLimits.get(company.id);
      if (existing) {
        this.companyLimitsFormArray.push(
          this.createCompanyLimitFormGroup(
            company.id,
            existing.limit,
            existing.isOverride
          )
        );
      } else {
        // Use the limit from the API response if available
        const apiLimit = company.maximumStudentsForMeetDay ?? null;
        this.companyLimitsFormArray.push(
          this.createCompanyLimitFormGroup(
            company.id,
            apiLimit,
            apiLimit !== null
          )
        );
      }
    });
  }

  private createCompanyLimitFormGroup(
    companyId: string,
    limit?: number | null,
    isOverride = false
  ): FormGroup {
    const effectiveLimit = limit !== undefined ? limit : null;

    return this.fb.group({
      companyId: [companyId],
      limit: [effectiveLimit, [Validators.min(1)]],
      isOverride: [isOverride],
    });
  }

  /**
   * Remove a company limit form group by company ID.
   * Called by Companies tab when a company is unregistered.
   */
  removeCompanyLimit(companyId?: string | null): void {
    if (!companyId) {
      return;
    }
    const index = this.companyLimitsFormArray.controls.findIndex(
      (control) => control.get("companyId")?.value === companyId
    );
    if (index >= 0) {
      this.companyLimitsFormArray.removeAt(index);
    }
  }

  /**
   * Reload entity data from the API.
   * Called by tab services after making changes that affect shared state.
   */
  reloadData(): void {
    const entity = this.entity();

    if (!entity.id) {
      return;
    }

    this.meetDayService
      .getMeetDay({ id: entity.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: MeetDay) => {
          this.entity.update((current) => ({
            ...current,
            studentsPerMeetdayRound: result.studentsPerMeetdayRound ?? null,
            maximumAmountOfStudentsForMeetDay:
              result.maximumAmountOfStudentsForMeetDay ?? null,
            meetDayRounds: result.meetDayRounds ?? [],
            registeredCompanies: result.registeredCompanies || [],
          }));

          this.meetDayForm.patchValue({
            studentsPerMeetdayRound: result.studentsPerMeetdayRound ?? null,
            maximumAmountOfStudentsForMeetDay:
              result.maximumAmountOfStudentsForMeetDay ?? null,
          });

          this.roundsFormArray.clear();
          if (result.meetDayRounds) {
            result.meetDayRounds.forEach((round) => {
              this.roundsFormArray.push(this.createRoundFormGroup(round));
            });
          }

          this.syncCompanyLimits(result.registeredCompanies || []);
        },
        error: (error) => {
          console.error("Failed to reload event details:", error);
          this.modal.Alert(
            "Error",
            "Failed to reload event details. Please refresh the page."
          );
        },
      });
  }

  // ========== Student Registrations (moved from MeetdayTabRegistrationsService) ==========

  /**
   * Load registered students for a meetday.
   */
  loadRegisteredStudents(meetDayId: string): void {
    if (!meetDayId) {
      return;
    }

    this.meetDayService
      .getMeetDayStudents({ meetDayId })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          console.error("Failed to load students:", error);
          return of({ data: [] });
        })
      )
      .subscribe((response) => {
        this.registeredStudents.set(response.data || []);
      });
  }

  /**
   * Export students to an Excel file.
   */
  exportStudents(meetDayId: string): void {
    if (!meetDayId) {
      return;
    }

    const endpoint = `meetday/${meetDayId}/student/export`;
    const defaultFilename = `meetday-${meetDayId}-students.xlsx`;

    this.fileDownloadService
      .downloadFromApiEndpoint(environment.apiUrl, endpoint, defaultFilename)
      .subscribe({
        error: (error) => {
          console.error("Failed to download students export:", error);
          this.modal.Alert(
            "Error",
            "Failed to download student export file. Please try again."
          );
        },
      });
  }
}
